"use client"

import { ReactElement, useEffect, useRef, useState } from "react"
import Script from "next/script"
import { usePathname } from "next/navigation"
import {
  getAnalyticsConfig,
  shouldLoadAnalytics,
} from "@modules/analytics/lib/config"
import {
  clearVendorCookies,
  readConsentFromDocument,
} from "@modules/analytics/lib/consent"
import { CONSENT_CHANGED_EVENT } from "@modules/analytics/lib/constants"
import { pageViewEvent } from "@modules/analytics/lib/events"
import { emitAnalyticsEvent, resolveSink } from "@modules/analytics/lib/pipeline"
import {
  resolveTags,
  warnAboutIgnoredConfig,
} from "@modules/analytics/lib/tags"
import type { AnalyticsTag } from "@modules/analytics/lib/tags"

/**
 * Casca cliente da analítica (Story 2.6, acesa pela 10.10). Sem lógica de decisão própria:
 * lê consent(document) + config(env), delega ao núcleo guardado e, quando o DUPLO GATE passa,
 * injeta os tags resolvidos. Regista um `page_view` na montagem e a cada troca de rota
 * (usePathname). NÃO usa useSearchParams — forçaria um CSR bailout/Suspense.
 *
 * Com env vazia (hoje) o gate é sempre falso ⇒ nenhum script de terceiro entra no DOM e nada
 * é empurrado (dormant). Preencher a env é o cutover.
 */
export default function AnalyticsLoader(): ReactElement | null {
  const pathname = usePathname()
  // Bump a cada mudança de consentimento (evento do banner), para re-emitir o page_view da
  // página ATUAL sem depender de navegação — cobre a landing view de quem consente na 1ª página.
  const [consentVersion, setConsentVersion] = useState(0)
  // Tags a injetar. Só são preenchidos DEPOIS do gate passar no cliente (o consentimento vive
  // num cookie que o servidor não lê aqui), por isso a 1ª renderização é sempre vazia.
  const [tags, setTags] = useState<AnalyticsTag[]>([])
  // Chave do último page_view emitido (versão de consentimento + rota). Torna o emit
  // idempotente: o deploy roda `next dev` com reactStrictMode (next.config.js), logo o
  // efeito de montagem é invocado DUAS vezes — sem esta guarda, a view de ENTRADA de um
  // visitante já-consentido empurraria 2 page_view, inflando ~2x a contagem de entrada no
  // cutover (viola o AC "um page_view por rota"). A chave inclui consentVersion para que
  // uma mudança REAL de consentimento ainda re-emita a rota atual (o ref sobrevive ao
  // remount simulado do StrictMode; a navegação muda pathname → nova chave → re-emite).
  const lastEmittedKey = useRef<string | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    const onConsentChange = (): void => setConsentVersion((version) => version + 1)
    window.addEventListener(CONSENT_CHANGED_EVENT, onConsentChange)
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onConsentChange)
  }, [])

  useEffect(() => {
    // Este efeito corre no ROOT LAYOUT: uma exceção aqui derruba a loja inteira (e basta uma
    // extensão do browser substituir `window.gtag`/`console.warn` por algo que lança). Exceção
    // localizada e justificada à regra "erros nunca engolidos" — o mesmo padrão do
    // purchase-tracker e do subscribe-button: analítica é não-crítica, a loja não é. O try
    // envolve o efeito INTEIRO (e não só o emit): ler o cookie, avisar do cutover e resolver os
    // tags acontecem todos aqui e nenhum deles pode custar a página.
    try {
      const consent = readConsentFromDocument()
      const config = getAnalyticsConfig()

      // Antes do gate de propósito: um id com typo deixa tudo dormant e é justamente aí que o
      // aviso de cutover tem de sair (uma vez por sessão).
      warnAboutIgnoredConfig(config)

      if (!shouldLoadAnalytics(consent, config)) {
        // Limpar os tags é o que fecha a CORRIDA da retirada: entre pedir o `<Script>` e ele
        // aterrar no DOM há uma janela em que `hasLoadedTagInDocument` responde `false`, logo
        // ninguém recarrega — e sem esta linha o `<Script>` continuaria montado e o gtag.js
        // carregaria DEPOIS do consentimento ter sido retirado.
        setTags((current) => (current.length > 0 ? [] : current))
        // INVARIANTE, verificada a cada carregamento sem consentimento: os cookies do terceiro
        // não podem existir. Apagá-los no clique da retirada não basta — o `gtag.js` continua
        // vivo até o `reload` concluir e reescreve o `_ga_<id>` entretanto (observado em
        // browser). Aqui já não há terceiro na página para os repor.
        clearVendorCookies()
        return
      }

      // Resolvidos UMA vez por efeito: servem o `<Script>` e o sink do evento.
      const resolvedTags = resolveTags(config)

      // Injeta o tag do vendor uma única vez: devolver o mesmo array quando já há tags evita
      // uma nova renderização a cada rota (e um `<Script>` re-montado a cada navegação).
      setTags((current) => (current.length > 0 ? current : resolvedTags))

      // Dedup StrictMode/remount: não re-emitir a MESMA (versão de consentimento, rota).
      const emitKey = `${consentVersion}:${pathname}`
      if (lastEmittedKey.current === emitKey) {
        return
      }
      lastEmittedKey.current = emitKey

      emitAnalyticsEvent(pageViewEvent(pathname), {
        consent,
        config,
        sink: resolveSink(resolvedTags),
      })
    } catch {
      // Falha de analítica é intencionalmente ignorada — não pode custar a página.
    }
    // consentVersion nas deps: re-emite o page_view atual quando o consentimento muda.
  }, [pathname, consentVersion])

  if (tags.length === 0) {
    return null
  }

  return (
    <>
      {tags.map((tag) => (
        // Só o loader do vendor: o bootstrap (`js`/`config`) é empurrado por CÓDIGO, junto do
        // primeiro evento (ver `createGtagSink`). Um `<Script>` inline correria DEPOIS dos
        // efeitos, e o GA4 descarta tudo o que chega à fila antes do `config`.
        // `afterInteractive`: a analítica nunca atrasa a interatividade da loja. O `next/script`
        // dedupa por `id` entre navegações de cliente.
        <Script
          key={tag.scriptId}
          id={tag.scriptId}
          src={tag.src}
          strategy="afterInteractive"
        />
      ))}
    </>
  )
}
